import React, {
	useEffect,
	useState,
	MouseEvent,
	useRef,
	useCallback,
} from "react";
import {observer} from "mobx-react-lite";
import styles from "./Theater.module.scss";
import {
	THEATER_RENDER_WIDTH,
	THEATER_RENDER_HEIGHT,
} from "../../state/userInputStore";
import {TheaterSize} from "../../state/theaterStore";
import {useCsContext} from "../../state/CsInstance";

import TheaterControls from "../TheaterControls/TheaterControls";
import CoordinateDisplay from "../CoordinateDisplay/CoordinateDisplay";
import TheaterApp from "../TheaterCanvas/TheaterApp";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import {ErrorBoundary} from "react-error-boundary";
import ErrorComponent from "../ErrorComponent/ErrorComponent";

import CompileErrorCurtain from "../CompileErrorCurtain/CompileErrorCurtain";
import FileTree from "../FileTree/FileTree";
import TheaterActivityTitle from "./TheaterActivityTitle";
import MultiplayerToggle from "./MultiplayerToggle";
const STAGE_CLASS = "csStage";

// This interface allows us to pass variables and callback function into a component
type Props = {};

const Theater: React.FC<Props> = () => {
	const csInstance = useCsContext();
	const {
		compileStore,
		theaterStore,
		userInputStore,
		multiplayerStore,
		getOrCreateMessenger,
	} = csInstance;

	// @ts-ignore no-unused-vars
	const [spriteManifest, setSpriteManifest] = useState("");

	const containerDivRef = useRef<HTMLDivElement | null>(null);

	const [, rerender] = useState(0);

	const displayLoading =
		compileStore.isCompiling || theaterStore.messengerLoading;

	// #######################
	// ### useEffect Hooks ###
	// #######################

	useEffect(() => {
		if (theaterStore.justStartedRunning) {
			const element = document.querySelector(
				`.${STAGE_CLASS}`
			) as HTMLCanvasElement;
			element.setAttribute("tabindex", "0"); // needs to be in here, for some reason
			element.focus();
		}
	}, [theaterStore.justStartedRunning]);

	// Helpers for window visibility change
	const [wasPreviouslyRunning, setWasPreviouslyRunning] = useState(false);
	const visibleListener = useCallback(() => {
		if (!(document.visibilityState === "visible")) {
			// just became non-visible
			if (theaterStore.isRunning) {
				setWasPreviouslyRunning(true);
			}
			theaterStore.isRunning = false;
		} else {
			// just became visible
			if (wasPreviouslyRunning) {
				theaterStore.isRunning = true;
				setWasPreviouslyRunning(false);
			}
		}
	}, [theaterStore, wasPreviouslyRunning]);

	useEffect(() => {
		window.addEventListener("resize", rerenderListener);
		window.addEventListener("visibilitychange", visibleListener);

		userInputStore.addKeyboardListeners();

		return () => {
			userInputStore.removeKeyboardListeners();
			window.removeEventListener("resize", rerenderListener);
			window.removeEventListener("visibilitychange", visibleListener);
		};
	}, [visibleListener, getOrCreateMessenger, userInputStore]);

	// Force pixi to rerender when the theater size changes
	useEffect(rerenderListener, [theaterStore.theaterSize]);

	// #############################
	// ### Callbacks and Helpers ###
	// #############################

	function rerenderListener() {
		rerender((prev) => prev + 1);
	}

	// Functions for the children Components to use to interact with this one
	function onMouseMoveTheaterCanvas(event: MouseEvent<HTMLDivElement>) {
		const target = event.target as HTMLCanvasElement;
		const {
			x: rectX,
			y: rectY,
			width: rectWidth,
			height: rectHeight,
		} = target.getBoundingClientRect();
		const {clientX: mouseX, clientY: mouseY} = event;

		const xPercent = (mouseX - rectX) / rectWidth;
		const yPercent = 1 - (mouseY - rectY) / rectHeight; // flip y-axis

		userInputStore.mouseX = (xPercent - 0.5) * THEATER_RENDER_WIDTH;
		userInputStore.mouseY = (yPercent - 0.5) * THEATER_RENDER_HEIGHT;
		userInputStore.mouseInBounds = true;
	}

	function onMouseOutTheaterCanvas() {
		userInputStore.mouseInBounds = false;
	}

	function onPointerDownTheaterCanvas() {
		userInputStore.isTheaterFocused = true;
		userInputStore.isMouseDown = true;
	}

	function onPointerUpTheaterCanvas() {
		userInputStore.isMouseDown = false;
	}

	async function onClickTheaterCanvas() {
		if (!theaterStore.isRunning) {
			const methods = await getOrCreateMessenger().getActorMethods(
				userInputStore.mouseX,
				userInputStore.mouseY
			);
			theaterStore.toggleShowManifest(true);
			theaterStore.methodManifest = methods;
		}
	}

	const containerDivWidth = containerDivRef.current?.clientWidth || 0;
	const theaterScale = containerDivWidth / THEATER_RENDER_WIDTH;
	const theaterClientWidth = THEATER_RENDER_WIDTH * theaterScale;

	useEffect(() => {
		userInputStore.updateTheaterScaleFromContainerDivWidth(containerDivWidth);
	}, [userInputStore, containerDivWidth]);

	const theaterColumnClassnames = [styles.theaterColumnWrapper];
	switch (theaterStore.theaterSize) {
		case TheaterSize.None:
			theaterColumnClassnames.push(styles.theaterColumnSizeNone);
			break;
		case TheaterSize.Small:
			theaterColumnClassnames.push(styles.theaterColumnSizeSmall);
			break;
		case TheaterSize.Medium:
			theaterColumnClassnames.push(styles.theaterColumnSizeMedium);
			break;
		case TheaterSize.Large:
			theaterColumnClassnames.push(styles.theaterColumnSizeLarge);
			break;
		case TheaterSize.Fullscreen:
			theaterColumnClassnames.push(styles.theaterColumnSizeFullscreen);
			break;
	}

	// don't let students interact with the theater as it doesn't reflect code
	const theaterInvalid = compileStore.isCompiling;

	return (
		<ErrorBoundary FallbackComponent={ErrorComponent}>
			<React.Fragment>
				<div
					ref={containerDivRef}
					className={theaterColumnClassnames.join(" ")}
				>
					<TheaterActivityTitle />
					{multiplayerStore.isMultiplayer && <MultiplayerToggle />}
					{!theaterClientWidth && (
						<div
							className={styles.theaterCanvasPlaceholder}
							style={{
								aspectRatio: `${THEATER_RENDER_WIDTH}/${THEATER_RENDER_HEIGHT}`,
							}}
						/>
					)}
					<div
						id="aopsTheaterCanvas"
						onMouseMove={onMouseMoveTheaterCanvas}
						onMouseOut={onMouseOutTheaterCanvas}
						onPointerDown={onPointerDownTheaterCanvas}
						onPointerUp={onPointerUpTheaterCanvas}
						onClick={onClickTheaterCanvas}
						className={[
							styles.theaterCanvas,
							theaterInvalid ? styles.theaterInvalid : "",
						].join(" ")}
					>
						<CoordinateDisplay />
						{containerDivRef.current && <TheaterApp />}
						{displayLoading && (
							<div className={styles.greyOut}>
								<LoadingScreen />
							</div>
						)}
						{compileStore.error && !theaterStore.runtimeErrorOccurred && (
							<CompileErrorCurtain />
						)}
					</div>
					<TheaterControls buttonClass={styles.buttonClass} />
					<FileTree />
				</div>
			</React.Fragment>
		</ErrorBoundary>
	);
};

export default observer(Theater);
